import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Input,
  Select,
} from 'antd';
import DatePicker from '../../components/DatePicker';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import moment from 'moment';
import './referralFilters.scss';

const DEBOUNCE_MS = 400;
const DEBOUNCED_FIELDS = ['patient', 'provider'];

const DEFAULT_FILTERS = {
  patient: '',
  provider: '',
  status: [],
  dob: '',
  date_from: '',
  date_to: '',
};

const hasActiveFilters = (f) => Object.entries(f).some(([, v]) => (Array.isArray(v) ? v.length > 0 : v !== ''));

const ReferralFilters = ({ filters, onFilterChange, availableStatuses }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const debounceTimer = useRef(null);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  useEffect(() => () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
  }, []);

  const handleChange = (name, value) => {
    const newFilters = { ...localFilters, [name]: value };
    setLocalFilters(newFilters);

    if (DEBOUNCED_FIELDS.includes(name)) {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => onFilterChange(newFilters), DEBOUNCE_MS);
    } else {
      onFilterChange(newFilters);
    }
  };

  const handleClear = () => {
    setLocalFilters(DEFAULT_FILTERS);
    onFilterChange(DEFAULT_FILTERS);
  };

  const statusOptions = availableStatuses.map((status) => ({ value: status, label: status }));

  return (
    <div className="referral-filters">
      <div className="referral-filters__search">
        <MagnifyingGlass size={14} className="referral-filters__search-icon" />
        <Input
          placeholder="Search patients..."
          value={localFilters.patient || ''}
          onChange={(e) => handleChange('patient', e.target.value)}
          className="referral-filters__input referral-filters__input--search"
        />
      </div>

      <Input
        placeholder="Practice / Provider"
        value={localFilters.provider || ''}
        onChange={(e) => handleChange('provider', e.target.value)}
        className="referral-filters__input referral-filters__input--provider"
      />

      <Select
        mode="multiple"
        value={localFilters.status || []}
        onChange={(value) => handleChange('status', value)}
        options={statusOptions}
        placeholder="All Statuses"
        allowClear
        maxTagCount={2}
        className="referral-filters__select"
      />

      <div className="referral-filters__date-group">
        <DatePicker
          placeholder="DOB"
          format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
          value={localFilters.dob ? moment(localFilters.dob, 'YYYY-MM-DD') : null}
          onChange={(date) => handleChange('dob', date ? date.format('YYYY-MM-DD') : '')}
          className="referral-filters__input referral-filters__input--date"
        />
      </div>

      <div className="referral-filters__date-group">
        <DatePicker.RangePicker
          placeholder={['Activity from', 'Activity to']}
          allowEmpty={[true, true]}
          format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
          value={[
            localFilters.date_from ? moment(localFilters.date_from, 'YYYY-MM-DD') : null,
            localFilters.date_to ? moment(localFilters.date_to, 'YYYY-MM-DD') : null,
          ]}
          onChange={(dates) => {
            const newFilters = {
              ...localFilters,
              date_from: dates?.[0] ? dates[0].format('YYYY-MM-DD') : '',
              date_to: dates?.[1] ? dates[1].format('YYYY-MM-DD') : '',
            };
            setLocalFilters(newFilters);
            onFilterChange(newFilters);
          }}
        />
      </div>

      {hasActiveFilters(localFilters) && (
        <button
          type="button"
          className="referral-filters__clear-btn"
          onClick={handleClear}
        >
          <X size={12} weight="bold" />
          Clear
        </button>
      )}
    </div>
  );
};

ReferralFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  availableStatuses: PropTypes.arrayOf(PropTypes.string),
};

ReferralFilters.defaultProps = {
  availableStatuses: [],
};

export default ReferralFilters;
